// src/components/Dashboard/StageTable.js
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ChartBarIcon,
  ClipboardIcon,
  EyeIcon,
  GlobeAltIcon,
  LockClosedIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../common/contexts/AuthContext";
import { firestore, functions } from "../../firebase";

const getStageURL = (slug) => `https://app.vdopage.com/s/${slug}`;

const formatDate = (createdDate) => {
  if (!createdDate) return "Not recorded";
  const date = createdDate.toDate
    ? createdDate.toDate()
    : createdDate.seconds
    ? new Date(createdDate.seconds * 1000)
    : new Date(createdDate);

  return Number.isNaN(date.getTime())
    ? "Not recorded"
    : date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

function PublishStatus({ isPublic }) {
  return isPublic ? (
    <span className="status-pill bg-emerald-50 text-emerald-700 ring-emerald-200">
      <GlobeAltIcon className="h-3.5 w-3.5" />
      Public
    </span>
  ) : (
    <span className="status-pill bg-slate-100 text-slate-600 ring-slate-200">
      <LockClosedIcon className="h-3.5 w-3.5" />
      Private
    </span>
  );
}

function StageActions({ event, loadingEventId, onToggle, onEdit, onDelete }) {
  const isUpdating = loadingEventId === event.id;
  const actionIconClass =
    "grid h-10 w-10 place-items-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950";
  const disabledIconClass =
    "grid h-10 w-10 place-items-center rounded-lg border border-slate-200 bg-slate-50 text-slate-300";
  const toggleClass = event.is_public
    ? "h-10 w-32 rounded-lg border border-rose-200 bg-white px-3 text-sm font-semibold text-rose-700 shadow-sm hover:bg-rose-50 disabled:opacity-60"
    : "h-10 w-32 rounded-lg border border-[#FF3A20] bg-[#FF3A20] px-3 text-sm font-semibold text-white shadow-sm hover:bg-[#e8321c] disabled:opacity-60";

  return (
    <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
      <Link
        to={`/p/${event.custom_slug}`}
        className={actionIconClass}
        aria-label={`Preview ${event.event_name || "page"}`}
        title="Preview"
      >
        <EyeIcon className="h-4 w-4" />
      </Link>

      {event.is_public ? (
        <Link
          to={`/s/${event.custom_slug}`}
          className={actionIconClass}
          aria-label={`Open ${event.event_name || "page"}`}
          title="Open public page"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </Link>
      ) : (
        <span
          className={disabledIconClass}
          aria-label="Open is available after publishing"
          title="Publish before opening the public page"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </span>
      )}

      <Link
        to={`/dashboard/pages/${event.id}/analytics`}
        className={actionIconClass}
        aria-label={`View analytics for ${event.event_name || "page"}`}
        title="Analytics"
      >
        <ChartBarIcon className="h-4 w-4" />
      </Link>

      <button
        onClick={() => onEdit?.(event)}
        className={actionIconClass}
        aria-label={`Edit ${event.event_name || "page"}`}
        title="Edit"
        type="button"
      >
        <PencilSquareIcon className="h-4 w-4" />
      </button>

      <button
        onClick={() => onToggle(event)}
        disabled={isUpdating}
        className={toggleClass}
        type="button"
      >
        {isUpdating
          ? "Updating..."
          : event.is_public
          ? "Make private"
          : "Make public"}
      </button>

      <button
        onClick={() => onDelete(event)}
        className={`${actionIconClass} text-rose-600 hover:text-rose-700`}
        aria-label={`Delete ${event.event_name || "page"}`}
        title="Delete"
        type="button"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function StageTable({ onCreateClick, onEditClick } = {}) {
  const { currentUser, accountData } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loadingEventId, setLoadingEventId] = useState(null);
  const [copiedEventId, setCopiedEventId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tableError, setTableError] = useState("");

  const toggleEventPublicStatus = async (eventId, isPublic) => {
    try {
      const togglePublicStatus = httpsCallable(
        functions,
        "toggleEventPublicStatus"
      );
      const result = await togglePublicStatus({ eventId, isPublic });
      console.log("STAGE_PUBLIC_STATUS_UPDATED", result.data.message);
      return result.data;
    } catch (error) {
      console.error("STAGE_PUBLIC_STATUS_FAILED", error.message);
      setTableError(error.message);
      return null;
    }
  };

  useEffect(() => {
    if (!currentUser || !accountData) return undefined;

    setIsLoading(true);
    setTableError("");

    const q = query(
      collection(firestore, "events"),
      where("user_id", "==", currentUser.uid),
      where("account_id", "==", accountData.id)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const eventsData = snapshot.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .sort((a, b) => {
            const aTime = a.created_date?.seconds || 0;
            const bTime = b.created_date?.seconds || 0;
            return bTime - aTime;
          });
        setEvents(eventsData);
        setIsLoading(false);
      },
      (error) => {
        console.error("STAGE_LISTEN_FAILED", error);
        setTableError("Pages could not be loaded. Refresh and try again.");
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, accountData]);

  const handleDelete = async () => {
    if (!selectedEvent) return;

    try {
      await deleteDoc(doc(firestore, "events", selectedEvent.id));
      setIsDeleteModalOpen(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error("STAGE_DELETE_FAILED", error);
      setTableError("Page could not be deleted. Try again.");
    }
  };

  const handleCopyURL = async (event) => {
    try {
      await navigator.clipboard.writeText(getStageURL(event.custom_slug));
      setCopiedEventId(event.id);
      window.setTimeout(() => setCopiedEventId(null), 1800);
    } catch (err) {
      console.error("STAGE_URL_COPY_FAILED", err);
      setTableError("Could not copy the URL from this browser.");
    }
  };

  const handleToggleClick = async (event) => {
    setLoadingEventId(event.id);
    setTableError("");
    await toggleEventPublicStatus(event.id, !event.is_public);
    setLoadingEventId(null);
  };

  const openDeleteModal = (event) => {
    setSelectedEvent(event);
    setIsDeleteModalOpen(true);
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-col gap-4 border-b border-slate-200 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
          Pages
        </h1>
        <div className="grid grid-cols-3 gap-3 sm:flex sm:items-stretch">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              Total
            </p>
            <p className="mt-1 font-mono text-2xl font-semibold text-slate-950">
              {events.length}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              Public
            </p>
            <p className="mt-1 font-mono text-2xl font-semibold text-slate-950">
              {events.filter((event) => event.is_public).length}
            </p>
          </div>
          {onCreateClick && (
            <button
              type="button"
              className="primary-button h-full min-h-[74px] flex-col px-4"
              onClick={onCreateClick}
              title="Create a new page"
            >
              <span className="text-xs uppercase tracking-[0.14em] opacity-80">New</span>
              <span className="mt-1">Create</span>
            </button>
          )}
        </div>
      </div>

      {tableError && (
        <div className="mx-5 mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 sm:mx-6">
          {tableError}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3 p-5 sm:p-6">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-20 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="p-5 sm:p-6">
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-lg bg-white text-slate-500 shadow-sm">
              <GlobeAltIcon className="h-6 w-6" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-slate-950">
              No pages yet
            </h3>
            {onCreateClick && (
              <button type="button" className="primary-button mt-5" onClick={onCreateClick}>
                Create page
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="hidden xl:block">
            <table className="dashboard-table w-full table-fixed divide-y divide-slate-200">
              <thead className="bg-slate-50/80">
                <tr>
                  <th scope="col" className="w-auto">Page</th>
                  <th scope="col" className="w-32">Status</th>
                  <th scope="col" className="w-36">Created</th>
                  <th scope="col" className="w-[420px] text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {events.map((event) => {
                  const stageURL = getStageURL(event.custom_slug);
                  const copied = copiedEventId === event.id;

                  return (
                    <tr key={event.id} className="hover:bg-slate-50/70">
                      <td>
                        <div className="min-w-0 pr-4">
                          <p className="truncate font-semibold text-slate-950">
                            {event.event_name || "Untitled page"}
                          </p>
                          <div className="mt-2 flex min-w-0 items-center gap-2">
                            <button
                              className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-950"
                              onClick={() => handleCopyURL(event)}
                              title="Copy URL"
                              type="button"
                            >
                              {copied ? (
                                <CheckIcon className="h-4 w-4 text-emerald-600" />
                              ) : (
                                <ClipboardIcon className="h-4 w-4" />
                              )}
                            </button>
                            <span className="min-w-0 truncate font-mono text-xs text-slate-500">
                              {stageURL}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <PublishStatus isPublic={event.is_public} />
                      </td>
                      <td className="font-medium text-slate-700">
                        {formatDate(event.created_date)}
                      </td>
                      <td className="w-[420px]">
                        <StageActions
                          event={event}
                          loadingEventId={loadingEventId}
                          onToggle={handleToggleClick}
                          onEdit={onEditClick}
                          onDelete={openDeleteModal}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid gap-4 p-5 sm:p-6 xl:hidden">
            {events.map((event) => {
              const copied = copiedEventId === event.id;

              return (
                <article key={event.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-semibold text-slate-950">
                        {event.event_name || "Untitled page"}
                      </h3>
                      <p className="mt-1 truncate font-mono text-xs text-slate-500">
                        /{event.custom_slug}
                      </p>
                    </div>
                    <PublishStatus isPublic={event.is_public} />
                  </div>

                  <div className="mt-4 rounded-lg bg-slate-50 p-3">
                    <div className="flex items-center gap-2">
                      <button
                        className="secondary-button px-2.5 py-2"
                        type="button"
                        onClick={() => handleCopyURL(event)}
                      >
                        {copied ? (
                          <CheckIcon className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <ClipboardIcon className="h-4 w-4" />
                        )}
                      </button>
                      <span className="min-w-0 truncate font-mono text-xs text-slate-500">
                        {getStageURL(event.custom_slug)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-4 text-sm">
                    <span className="font-medium text-slate-500">Created</span>
                    <span className="font-semibold text-slate-800">
                      {formatDate(event.created_date)}
                    </span>
                  </div>

                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <StageActions
                      event={event}
                      loadingEventId={loadingEventId}
                      onToggle={handleToggleClick}
                      onEdit={onEditClick}
                      onDelete={openDeleteModal}
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}

      <Transition.Root show={isDeleteModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-4 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-xl bg-white p-6 text-left shadow-2xl transition-all">
                  <div className="flex items-start gap-4">
                    <div className="grid h-12 w-12 flex-none place-items-center rounded-lg bg-rose-50 text-rose-600">
                      <TrashIcon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-slate-950">
                        Delete page
                      </Dialog.Title>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        Delete{" "}
                        <span className="font-semibold text-slate-950">
                          {selectedEvent?.event_name || "this page"}
                        </span>
                        ?
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setIsDeleteModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="danger-button"
                      onClick={handleDelete}
                    >
                      Delete page
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
